import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { CreditCard as CreditCardIcon } from '@mui/icons-material';
import { createPaymentIntent, eurosToCents } from '../../services/paymentService';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: false
};

const StripePayment = ({
  amount,
  orderData,
  onPaymentSuccess,
  onPaymentError,
  isProcessing,
  disabled = false
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const formatPrice = (price) =>
    new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('El sistema de pago no está disponible. Recarga la página.');
      return;
    }

    const card = elements.getElement(CardElement);
    if (!card) {
      setError('Introduce los datos de la tarjeta');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const amountCents = eurosToCents(amount);
      const intentData = await createPaymentIntent(amountCents, {
        customerEmail: orderData.email,
        customerName: orderData.name
      });

      // Modo desarrollo: Functions sin clave secreta de Stripe
      if (intentData.devMode) {
        if (process.env.NODE_ENV === 'production') {
          setError('El servicio de pago no está disponible. Contacta con la tienda.');
          onPaymentError?.(new Error('Pagos no configurados en producción'));
          return;
        }

        const { error: tokenError, token } = await stripe.createToken(card, {
          name: orderData.name,
          address_line1: orderData.address,
          address_city: orderData.city,
          address_zip: orderData.postalCode,
          address_country: 'ES'
        });

        if (tokenError) {
          setError(tokenError.message);
          onPaymentError?.(tokenError);
          return;
        }

        onPaymentSuccess?.({
          id: intentData.paymentIntentId,
          method: 'stripe',
          devMode: true,
          paymentStatus: 'succeeded',
          tokenId: token?.id
        });
        return;
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        intentData.clientSecret,
        {
          payment_method: {
            card,
            billing_details: {
              name: orderData.name,
              email: orderData.email,
              phone: orderData.phone,
              address: {
                line1: orderData.address,
                city: orderData.city,
                postal_code: orderData.postalCode,
                country: 'ES'
              }
            }
          }
        }
      );

      if (confirmError) {
        setError(confirmError.message);
        onPaymentError?.(confirmError);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        onPaymentSuccess?.({
          id: paymentIntent.id,
          method: 'stripe',
          devMode: false,
          paymentStatus: paymentIntent.status
        });
      } else {
        setError('El pago no se completó. Inténtalo de nuevo.');
        onPaymentError?.(new Error('Pago incompleto'));
      }
    } catch (err) {
      console.error('Error procesando pago:', err);
      const message =
        err.message ||
        'Error al conectar con el servidor de pagos. ¿Están desplegadas las Cloud Functions?';
      setError(message);
      onPaymentError?.(err);
    } finally {
      setProcessing(false);
    }
  };

  const isDisabled = !stripe || processing || isProcessing || disabled;

  return (
    <Paper sx={{ p: 3, width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CreditCardIcon />
        Pago con tarjeta
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Box
          sx={{
            p: 2,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            mb: 3
          }}
        >
          <CardElement
            options={CARD_ELEMENT_OPTIONS}
            onChange={(event) => {
              setError(event.error ? event.error.message : null);
            }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={isDisabled}
          startIcon={
            processing || isProcessing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CreditCardIcon />
            )
          }
          sx={{ py: 1.5 }}
        >
          {processing || isProcessing
            ? 'Procesando pago...'
            : `Pagar ${formatPrice(amount)}`}
        </Button>
      </Box>
    </Paper>
  );
};

export default StripePayment;
